import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { ChevronDown, ChevronRight, Wallet, TrendingUp } from "lucide-react";

function pence(p: number) {
  return `£${(p / 100).toFixed(2)}`;
}

type Driver = {
  driverId: number;
  name: string;
  email: string | null;
  foodDeliveryEarnedPence: number;
  itemDeliveryEarnedPence: number;
  totalEarnedPence: number;
  totalWithdrawnPence: number;
  walletBalancePence: number;
};

type PlatformBreakdown = {
  customerPlatformFeesPence: number;
  hubDriverFeesPence: number;
  serviceChargesPence: number;
  multiStoreStripeRecoveryPence: number;
  deliveredOrderCount: number;
};

type WalletData = {
  drivers: Driver[];
  totalDriverWalletPence: number;
  platformIncomePence: number;
  platformBreakdown: PlatformBreakdown;
};

export default function AdminDriverWallets() {
  useAuth();
  const { toast } = useToast();
  const token = localStorage.getItem("ayrshire_token");
  const [data, setData] = useState<WalletData | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  async function load() {
    setLoading(true);
    try {
      const res = await fetch("/api/admin/driver-wallets", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to load");
      setData(await res.json() as WalletData);
    } catch {
      toast({ title: "Error", description: "Could not load driver wallet data", variant: "destructive", action: <ToastAction altText="Reload page" onClick={() => window.location.reload()}>Reload</ToastAction> });
    } finally {
      setLoading(false);
    }
  }

  function toggleExpand(driverId: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(driverId) ? next.delete(driverId) : next.add(driverId);
      return next;
    });
  }

  return (
    <AdminLayout current="/admin/driver-wallets">
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Driver Wallets</h1>
          <p className="text-sm text-gray-500 mt-1">
            Money owed to drivers (earnings minus completed withdrawals). This is money sitting in your bank account that belongs to drivers — do not spend it.
          </p>
        </div>

        {!data && !loading && (
          <button
            onClick={() => void load()}
            className="px-4 py-2 bg-[#1e3a5f] text-white rounded-lg text-sm font-semibold hover:bg-[#162d4a]"
          >
            Load Wallets
          </button>
        )}

        {loading && (
          <div className="text-center py-12 text-gray-400 text-sm">Loading…</div>
        )}

        {data && !loading && (
          <>
            {/* Summary banners */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Money you owe drivers */}
              <div className="rounded-xl p-5 bg-red-50 dark:bg-red-900/20 border border-red-200">
                <div className="flex items-center gap-3 mb-2">
                  <Wallet className="text-red-500 shrink-0" size={24} />
                  <span className="text-sm font-semibold text-red-700 uppercase tracking-wide">Owed to Drivers</span>
                </div>
                <div className="text-3xl font-bold text-red-700">{pence(data.totalDriverWalletPence)}</div>
                <div className="text-xs text-gray-500 mt-1">
                  Across {data.drivers.length} driver{data.drivers.length !== 1 ? "s" : ""} · do not spend this
                </div>
              </div>

              {/* Platform income */}
              <div className="rounded-xl p-5 bg-green-50 dark:bg-green-900/20 border border-green-200">
                <div className="flex items-center gap-3 mb-2">
                  <TrendingUp className="text-green-600 shrink-0" size={24} />
                  <span className="text-sm font-semibold text-green-700 uppercase tracking-wide">Your Platform Income</span>
                </div>
                <div className="text-3xl font-bold text-green-700">{pence(data.platformIncomePence)}</div>
                <div className="text-xs text-gray-500 mt-1">
                  From {data.platformBreakdown.deliveredOrderCount.toLocaleString()} delivered orders · this is yours to keep
                </div>
              </div>
            </div>

            {/* Platform income breakdown */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
              <div className="text-sm font-semibold text-gray-700">Platform Income Breakdown</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">Platform Fees</div>
                  <div className="font-bold text-gray-800">{pence(data.platformBreakdown.customerPlatformFeesPence)}</div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">Delivery Fees (hub cut)</div>
                  <div className="font-bold text-gray-800">{pence(data.platformBreakdown.hubDriverFeesPence)}</div>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">Service Charges</div>
                  <div className="font-bold text-gray-800">{pence(data.platformBreakdown.serviceChargesPence)}</div>
                </div>
                {(data.platformBreakdown.multiStoreStripeRecoveryPence ?? 0) > 0 && (
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <div className="text-xs text-blue-600 mb-1">Multi-Order Stripe Recovery</div>
                    <div className="font-bold text-blue-800">{pence(data.platformBreakdown.multiStoreStripeRecoveryPence)}</div>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-400">
                Platform income is cumulative from all delivered orders. Multi-order Stripe recovery (20p + 1.5% per store) partially offsets processing fees charged by Stripe.
              </p>
            </div>

            {/* Driver breakdown */}
            {data.drivers.length === 0 ? (
              <div className="text-center py-12 text-gray-400 text-sm">No outstanding driver wallet balances.</div>
            ) : (
              <div className="space-y-3">
                <div className="text-sm font-semibold text-gray-700">Per-Driver Breakdown</div>
                {data.drivers.map((driver) => {
                  const isOpen = expanded.has(driver.driverId);
                  return (
                    <div key={driver.driverId} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                      <div className="flex items-center gap-3 px-5 py-4">
                        <button
                          onClick={() => toggleExpand(driver.driverId)}
                          className="text-gray-400 hover:text-gray-600 shrink-0"
                        >
                          {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-foreground">{driver.name}</div>
                          <div className="text-xs text-gray-400">{driver.email ?? "No email"}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-red-600 text-lg">{pence(driver.walletBalancePence)}</div>
                          <div className="text-xs text-gray-400">owed</div>
                        </div>
                      </div>

                      {isOpen && (
                        <div className="border-t border-gray-100 px-5 py-4 bg-gray-50 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                          <div>
                            <div className="text-xs text-gray-500 mb-0.5">Food delivery earned</div>
                            <div className="font-semibold text-gray-800">{pence(driver.foodDeliveryEarnedPence)}</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 mb-0.5">Item delivery earned</div>
                            <div className="font-semibold text-gray-800">{pence(driver.itemDeliveryEarnedPence)}</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 mb-0.5">Total earned</div>
                            <div className="font-semibold text-gray-800">{pence(driver.totalEarnedPence)}</div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 mb-0.5">Withdrawn</div>
                            <div className="font-semibold text-gray-800">{pence(driver.totalWithdrawnPence)}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <button
              onClick={() => void load()}
              className="text-sm text-gray-500 underline hover:text-gray-700"
            >
              Refresh
            </button>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
